import { notifyContentUpdated } from './contentEvents';

/** In dev, use CRA proxy (package.json → localhost:3050). In prod, Vercel backend. */
const API_BASE =
  process.env.REACT_APP_CONTENT_API_URL ??
  (process.env.NODE_ENV === 'development' ? '' : 'https://databiqs-website-backend.vercel.app');

export const ADMIN_STORAGE_KEY = 'databiqs_admin_token';
const ADMIN_API_ORIGIN_KEY = 'databiqs_admin_api_origin';
const BACKEND_OFFLINE_MSG =
  'Backend is not running on port 3050. Open a second terminal in databiqs-website and run: npm run server';

/** Thrown when the server rejects the JWT (wrong API, secret rotation, or expiry). */
export class AdminSessionError extends Error {
  constructor(message = 'Session expired. Please sign in again.') {
    super(message);
    this.name = 'AdminSessionError';
    this.code = 'SESSION_EXPIRED';
  }
}

export function isAdminSessionError(err) {
  return err?.code === 'SESSION_EXPIRED' || err?.name === 'AdminSessionError';
}

function getAdminApiOrigin() {
  if (API_BASE) return API_BASE.replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

export function getAdminToken() {
  try {
    const token = sessionStorage.getItem(ADMIN_STORAGE_KEY);
    if (!token) return null;
    const storedOrigin = sessionStorage.getItem(ADMIN_API_ORIGIN_KEY);
    const currentOrigin = getAdminApiOrigin();
    if (storedOrigin && currentOrigin && storedOrigin !== currentOrigin) {
      setAdminToken(null);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function setAdminToken(token) {
  try {
    if (token) {
      sessionStorage.setItem(ADMIN_STORAGE_KEY, token);
      sessionStorage.setItem(ADMIN_API_ORIGIN_KEY, getAdminApiOrigin());
    } else {
      sessionStorage.removeItem(ADMIN_STORAGE_KEY);
      sessionStorage.removeItem(ADMIN_API_ORIGIN_KEY);
    }
  } catch {
    /* ignore */
  }
}

async function readJson(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function throwIfBackendOffline(res, data, fallbackMessage) {
  const looksLikeProxyCrash = res.status >= 500 && !data?.error;
  if (looksLikeProxyCrash) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(BACKEND_OFFLINE_MSG);
    }
    throw new Error('Backend server error. Please try again in a moment.');
  }
  throw new Error(data?.error || fallbackMessage);
}

async function parseAdminResponse(res) {
  const data = await readJson(res);
  if (res.status === 401) {
    setAdminToken(null);
    const msg = data.error || 'Session expired. Please sign in again.';
    if (/invalid|expired|unauthorized/i.test(msg)) {
      throw new AdminSessionError('Your admin session expired or was issued by a different server. Please sign in again.');
    }
    throw new AdminSessionError(msg);
  }
  if (!res.ok) throwIfBackendOffline(res, data, 'Request failed');
  return data;
}

function adminAuthHeaders() {
  const token = getAdminToken();
  if (!token) throw new AdminSessionError('Not signed in. Please open the admin login page and sign in again.');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function checkApiHealth() {
  try {
    const res = await fetch(`${API_BASE}/api/health`, { cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchPublicContent(cacheBust) {
  const q = cacheBust ? `?t=${encodeURIComponent(cacheBust)}` : '';
  let res;
  try {
    res = await fetch(`${API_BASE}/api/content${q}`, { cache: 'no-store' });
  } catch {
    throw new Error(BACKEND_OFFLINE_MSG);
  }
  const data = await readJson(res);
  if (!res.ok) throwIfBackendOffline(res, data, 'Failed to load content');
  return data;
}

export async function adminLogin(email, password) {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new Error(BACKEND_OFFLINE_MSG);
  }
  const data = await readJson(res);
  if (!res.ok) throwIfBackendOffline(res, data, 'Login failed');
  setAdminToken(data.token);
  return data;
}

export async function fetchAdminContent() {
  const res = await fetch(`${API_BASE}/api/admin/content`, {
    cache: 'no-store',
    headers: adminAuthHeaders(),
  });
  return parseAdminResponse(res);
}

export async function saveAdminContent(content) {
  const payload = {
    ...content,
    updatedAt: new Date().toISOString(),
  };
  const res = await fetch(`${API_BASE}/api/admin/content`, {
    method: 'PUT',
    headers: adminAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseAdminResponse(res);
  notifyContentUpdated(payload.updatedAt);
  return { ...data, content: payload };
}

async function fallbackSaveFullSection(section, payload) {
  let current = {};
  try {
    current = await fetchAdminContent();
  } catch {
    current = {};
  }
  const updatedFullContent = {
    ...current,
    [section]: payload,
    updatedAt: new Date().toISOString(),
  };
  const result = await saveAdminContent(updatedFullContent);
  return { ok: true, section, updatedAt: result.updatedAt };
}

export async function saveAdminSection(section, payload) {
  // Directly use full content PUT for extended sections (aboutPage, servicePages, homePage)
  // to bypass old backend PATCH section validation restrictions.
  const isExtendedSection = ['aboutPage', 'servicePages', 'homePage', 'servicesPage'].includes(section);
  if (isExtendedSection) {
    return fallbackSaveFullSection(section, payload);
  }

  let res;
  try {
    res = await fetch(`${API_BASE}/api/admin/content/${section}`, {
      method: 'PATCH',
      headers: adminAuthHeaders(),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (isAdminSessionError(err)) throw err;
    return fallbackSaveFullSection(section, payload);
  }

  const data = await readJson(res);
  if (res.status === 401) {
    setAdminToken(null);
    throw new AdminSessionError('Your admin session expired. Please sign in again.');
  }

  if (!res.ok) {
    return fallbackSaveFullSection(section, payload);
  }

  notifyContentUpdated(data.updatedAt);
  return data;
}
